/**
 * Choose phase of the step-up authentication modal.
 * Shows enrolled MFA methods and lets the user pick one to challenge.
 *
 * Presentational only — the parent modal owns the API call. Ported from
 * shipstation-lite/src/features/mfa/choose-phase.tsx, widened with the
 * `phoneVerificationState` (G4) copy variants from
 * ss-ui-client/src/global/step-up-authentication/phases/choose/choose-phase-body.jsx
 */

import { useState } from 'react';
import { Button, Radio, Stack, Text } from '@mantine/core';
import { FormattedMessage } from 'react-intl';

import { MfaErrorBanner } from './mfa-error-banner';
import { ModalContent } from '../../components/modal-content';
import { ModalFooter } from '../../components/modal-footer';
import { PHONE_VERIFICATION_STATES } from '../mfa-constants';
import { getEnrollmentFactorFallbackText, getEnrollmentFactorMessage } from '../mfa-factor-messages';
import { mfaMessages } from '../mfa-messages';

import type { PhoneVerificationState } from '../mfa-constants';
import type { MfaEnrollment } from '../mfa-types';

export type ChoosePhaseProps = {
  enrollments: MfaEnrollment[];
  isSubmitting: boolean;
  errorMessage?: string;
  /** Backend error code, rendered alongside `errorMessage`. */
  errorCode?: string;
  onSelect: (enrollment: MfaEnrollment) => void;
  phoneVerificationState?: PhoneVerificationState;
};

const FORCED_MFA_PHONE_VERIFICATION_STATES: PhoneVerificationState[] = [
  PHONE_VERIFICATION_STATES.BANNER,
  PHONE_VERIFICATION_STATES.MODAL,
  PHONE_VERIFICATION_STATES.UNSKIPPABLE,
];

/** Radio label: a factor message when one applies, otherwise plain text. */
const EnrollmentFactorLabel = ({ enrollment }: { enrollment: MfaEnrollment }) => {
  const factorMessage = getEnrollmentFactorMessage(enrollment);

  if (!factorMessage) {
    return <>{getEnrollmentFactorFallbackText(enrollment)}</>;
  }

  const values = { lastFour: enrollment.name.slice(-4), maskedEmail: enrollment.name };
  return <FormattedMessage {...factorMessage} values={values} />;
};

export const ChoosePhase = ({ enrollments, isSubmitting, errorMessage, errorCode, onSelect, phoneVerificationState }: ChoosePhaseProps) => {
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState(enrollments[0]?.id ?? '');

  const shouldShowForcedMfaContext =
    phoneVerificationState != null && FORCED_MFA_PHONE_VERIFICATION_STATES.includes(phoneVerificationState);

  const handleSendCode = () => {
    const selectedEnrollment = enrollments.find((enrollment) => enrollment.id === selectedEnrollmentId);
    if (!selectedEnrollment || isSubmitting) {
      return;
    }
    onSelect(selectedEnrollment);
  };

  return (
    <>
      <ModalContent>
        <Text fw={600} size='lg'>
          <FormattedMessage {...mfaMessages.chooseTitle} />
        </Text>

        {shouldShowForcedMfaContext ? (
          <Text fw={600} size='md'>
            <FormattedMessage {...mfaMessages.forcedMfaFlowContext} />
          </Text>
        ) : null}

        <Text size='sm'>
          {shouldShowForcedMfaContext ? (
            <FormattedMessage {...mfaMessages.forcedMfaChooseSubheader} />
          ) : (
            <FormattedMessage {...mfaMessages.chooseSubheader} />
          )}
        </Text>

        <Radio.Group onChange={setSelectedEnrollmentId} value={selectedEnrollmentId}>
          <Stack gap='sm'>
            {enrollments.map((enrollment) => (
              <Radio
                key={enrollment.id}
                disabled={isSubmitting}
                label={<EnrollmentFactorLabel enrollment={enrollment} />}
                value={enrollment.id}
              />
            ))}
          </Stack>
        </Radio.Group>

        {errorMessage ? <MfaErrorBanner code={errorCode} message={errorMessage} /> : null}

        {shouldShowForcedMfaContext ? (
          <Text size='sm'>
            <FormattedMessage {...mfaMessages.securityExplanation} />
          </Text>
        ) : null}
      </ModalContent>

      <ModalFooter>
        <Button disabled={isSubmitting || !selectedEnrollmentId} onClick={handleSendCode}>
          <FormattedMessage {...mfaMessages.sendCodeButton} />
        </Button>
      </ModalFooter>
    </>
  );
};
